import type {
  CoachSessionAttendance,
  CoachAttendanceSessionRow,
  CoachAttendanceStatus,
} from '../types';

/** Past-day window for camp and standalone private sessions in coach attendance reports. */
export const COACH_ATTENDANCE_PRIVATE_LOOKBACK_DAYS = 30;

export const COACH_ATTENDANCE_STATUSES = [
  'scheduled',
  'attended',
  'missed',
  'cancelled',
] as const satisfies readonly CoachAttendanceStatus[];

/** Default session list filter: past/actionable sessions; upcoming scheduled hidden. */
export const COACH_ATTENDANCE_DEFAULT_FILTER_STATUSES: CoachAttendanceStatus[] = [
  'attended',
  'missed',
  'cancelled',
];

/** Shown on hover when student attendance is incomplete before coach self check-in. */
export const COACH_SELF_CHECK_IN_REQUIRES_STUDENT_ATTENDANCE_MSG =
  'Mark attendance for every student before you can check in.';

/** Shown after check-in is pressed when the session has no enrolled students. */
export const COACH_SELF_CHECK_IN_NO_STUDENTS_MSG =
  'There are no enrolled students for this session, so check-in is not available.';

export type StudentSessionAttendanceStatus = 'scheduled' | 'attended' | 'missed' | 'cancelled';

export type CoachSelfCheckInRosterBlockReason = 'none' | 'no_students' | 'incomplete_attendance';

export function isStudentSessionAttendanceMarked(
  status: StudentSessionAttendanceStatus | undefined | null
): boolean {
  return status === 'attended' || status === 'missed' || status === 'cancelled';
}

export function countMarkedStudentAttendance(
  statuses: Array<StudentSessionAttendanceStatus | undefined | null>
): number {
  return statuses.filter(isStudentSessionAttendanceMarked).length;
}

export function getCoachSelfCheckInRosterBlockReason(
  rosterCount: number,
  markedCount: number
): CoachSelfCheckInRosterBlockReason {
  if (rosterCount === 0) return 'no_students';
  if (markedCount < rosterCount) return 'incomplete_attendance';
  return 'none';
}

/** Disable check-in button when some students still need attendance marks. */
export function shouldDisableCoachSelfCheckInForRoster(
  rosterCount: number,
  markedCount: number
): boolean {
  return (
    getCoachSelfCheckInRosterBlockReason(rosterCount, markedCount) === 'incomplete_attendance'
  );
}

export function isCoachAttendanceStatus(value: string): value is CoachAttendanceStatus {
  return (COACH_ATTENDANCE_STATUSES as readonly string[]).includes(value);
}

export function parseCoachAttendanceStatusList(value: string): CoachAttendanceStatus[] | null {
  const parts = value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const parsed = parts.filter(isCoachAttendanceStatus);
  return parsed.length > 0 ? parsed : null;
}

export interface CoachAttendanceRateInput {
  assigned: number;
  attended: number;
}

/** Attendance rate as percentage (0–100). Defaults to 100 when nothing assigned. */
export function computeCoachAttendanceRate(input: CoachAttendanceRateInput): number {
  const { assigned, attended } = input;
  if (assigned <= 0) return 100;
  return Math.round((attended / assigned) * 1000) / 10;
}

export interface CoachAttendanceSummaryCounts {
  assignedSessions: number;
  attendedSessions: number;
  missedSessions: number;
  cancelledSessions: number;
}

/** Derive summary counts from session rows (resolved status per session). */
export function summarizeCoachAttendanceFromRows(
  rows: Array<{ status: CoachAttendanceStatus }>
): CoachAttendanceSummaryCounts {
  let attendedSessions = 0;
  let missedSessions = 0;
  let cancelledSessions = 0;

  for (const row of rows) {
    if (row.status === 'attended') attendedSessions++;
    else if (row.status === 'missed') missedSessions++;
    else if (row.status === 'cancelled') cancelledSessions++;
  }

  return {
    assignedSessions: rows.length,
    attendedSessions,
    missedSessions,
    cancelledSessions,
  };
}

/** Status for editing UI: only explicit marks; unmarked = scheduled (no toggle selected). */
export function resolveCoachSessionDisplayStatus(
  stored: CoachSessionAttendance | undefined
): CoachAttendanceStatus {
  return stored?.status ?? 'scheduled';
}

/** Merge assigned session list with stored attendance; unmarked past sessions → missed. */
export function resolveCoachSessionStatus(
  stored: CoachSessionAttendance | undefined,
  sessionDateYmd: string,
  todayYmd: string
): CoachAttendanceStatus {
  if (stored?.status) {
    if (stored.status === 'scheduled' && sessionDateYmd <= todayYmd) return 'missed';
    return stored.status;
  }
  if (sessionDateYmd <= todayYmd) return 'missed';
  return 'scheduled';
}

export function buildCoachAttendanceSessionRows(
  assigned: Array<{
    sessionId: string;
    lessonId: string;
    lessonName: string;
    lessonType: 'group' | 'private' | 'camp';
    sessionDate: string;
    startTime: string;
    endTime: string;
  }>,
  attendanceBySessionId: Map<string, CoachSessionAttendance>,
  todayYmd: string
): CoachAttendanceSessionRow[] {
  return assigned.map((s) => {
    const stored = attendanceBySessionId.get(s.sessionId);
    return {
      ...s,
      status: resolveCoachSessionStatus(stored, s.sessionDate, todayYmd),
      source: stored?.source,
    };
  });
}

export interface EnrichCoachSessionRowForSelfCheckInOptions {
  coachUserId?: string;
  todayYmd: string;
  allowCoachSelfAttendance?: boolean;
}

/** Add self check-in UI flags to a coach attendance session row. */
export function enrichCoachSessionRowForSelfCheckIn(
  row: CoachAttendanceSessionRow,
  stored: CoachSessionAttendance | undefined,
  options: EnrichCoachSessionRowForSelfCheckInOptions
): CoachAttendanceSessionRow {
  const { coachUserId, todayYmd, allowCoachSelfAttendance = false } = options;
  if (!coachUserId || !allowCoachSelfAttendance) {
    return {
      ...row,
      checkedIn: stored?.status === 'attended',
      selfCheckInLocked: false,
      selfCheckInAvailable: false,
    };
  }

  const checkedIn = stored?.status === 'attended';
  const staffLocked = stored?.source === 'manual';
  const selfCheckInAvailable = row.sessionDate <= todayYmd && !staffLocked;

  return {
    ...row,
    checkedIn,
    selfCheckInLocked: staffLocked,
    selfCheckInAvailable,
  };
}

export interface CoachSessionSelfAttendanceStatus {
  status: CoachAttendanceStatus;
  checkedIn: boolean;
  selfCheckInAvailable: boolean;
  selfCheckInLocked: boolean;
}

/** Self check-in status for a single session (coach schedule modal). */
export function buildCoachSessionSelfAttendanceStatus(
  stored: CoachSessionAttendance | undefined,
  sessionDateYmd: string,
  todayYmd: string,
  coachUserId: string | undefined,
  allowCoachSelfAttendance: boolean
): CoachSessionSelfAttendanceStatus {
  const status = resolveCoachSessionStatus(stored, sessionDateYmd, todayYmd);
  const enriched = enrichCoachSessionRowForSelfCheckIn(
    {
      sessionId: stored?.sessionId ?? '',
      lessonId: '',
      lessonName: '',
      lessonType: 'group',
      sessionDate: sessionDateYmd,
      startTime: '',
      endTime: '',
      status,
      source: stored?.source,
    },
    stored,
    { coachUserId, todayYmd, allowCoachSelfAttendance }
  );
  return {
    status,
    checkedIn: enriched.checkedIn ?? false,
    selfCheckInAvailable: enriched.selfCheckInAvailable ?? false,
    selfCheckInLocked: enriched.selfCheckInLocked ?? false,
  };
}
